import { PanelType } from "@/shared/state/visualization";

import { LayoutNavigationState } from "../../navigation/layoutNavigation";
import { LayoutDefinitionBuilder } from "../layouts/LayoutDefinitionBuilder";
import {
  getCorrespondingGeoType,
  MessagePathRecord,
  TopicRecord,
} from "../topics";

/**
 * Builder to create a {@link LayoutNavigationState} for deep linking into the visualizer.
 *
 * Usage:
 * ```ts
 * const workspaceInput = new WorkspaceBuilder().build();
 * ```
 *
 * Only supports single direction layouts at the moment. To create a nested layouts right now,
 * the user should create the workspaceInput with the desired panels and then manually
 * update the layout property to their desired layout.
 */
export class WorkspaceBuilder {
  #layoutBuilder: LayoutDefinitionBuilder;

  constructor() {
    this.#layoutBuilder = new LayoutDefinitionBuilder();
  }

  public addImagePanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    return this.addMultiSignalPanel({
      type: PanelType.Image,
      signals: [
        {
          topicRecord: options.topicRecord,
          messagePathRecord: options.messagePathRecord,
        },
      ],
      id: options.id,
      title: options.title,
    });
  }

  public addLogPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    return this.addMultiSignalPanel({
      type: PanelType.Log,
      signals: [
        {
          topicRecord: options.topicRecord,
          messagePathRecord: options.messagePathRecord,
        },
      ],
      id: options.id,
      title: options.title,
    });
  }

  public addMapPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.source_path}`;

    const correspondingTypes = getCorrespondingGeoType(
      messagePathRecord.canonical_data_type,
    );

    const correspondingMessagePath = topicRecord.message_paths.find(
      (messagePath) =>
        correspondingTypes.includes(messagePath.canonical_data_type),
    );

    const requirements = [
      LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
        topicRecord,
        messagePathRecord,
      ),
    ];

    if (correspondingMessagePath) {
      requirements.push(
        LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
          topicRecord,
          correspondingMessagePath,
        ),
      );
    }

    const ruleId = this.#layoutBuilder.addRule(requirements);
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.Map,
      },
      [ruleId],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  /**
   * Add a single panel that visualizes several signals at once (e.g. an IMU's
   * x/y/z on one plot).
   *
   * Plot, Log, RawMessage, and Image panels get one single-requirement rule
   * per signal, so each signal matches independently. Log hydration keeps
   * only the first matched topic's columns, so log signals must share a
   * topic — callers validate this (see `validateSignalsForNewPanel`).
   *
   * Map panels get one two-requirement rule per track. The matcher pairs a
   * multi-requirement rule's matches by index into one map path, so a single
   * rule carrying every requirement would collapse a whole fleet into one
   * lat/lon track and drop the rest. Map signals therefore arrive as
   * latitude/longitude pairs in role order — `validateSignalsForNewPanel`
   * emits them that way — and each pair becomes its own rule, which then
   * binds one track per file that carries the topic.
   */
  public addMultiSignalPanel(options: {
    type:
      | PanelType.Plot
      | PanelType.Log
      | PanelType.RawMessage
      | PanelType.Image
      | PanelType.Map;
    signals: {
      topicRecord: TopicRecord;
      messagePathRecord: MessagePathRecord;
    }[];
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { type, signals } = options;
    if (signals.length === 0) {
      throw new Error("At least one signal is required to add a panel.");
    }

    const id = options.id ?? crypto.randomUUID();
    const [first] = signals;
    const title =
      options.title ??
      (signals.length === 1
        ? `${first.topicRecord.topic_name}.${first.messagePathRecord.source_path}`
        : `${first.topicRecord.topic_name} (${signals.length} signals)`);

    const requirements = signals.map((signal) =>
      LayoutDefinitionBuilder.createRequirementFromMessagePathRecord(
        signal.topicRecord,
        signal.messagePathRecord,
      ),
    );

    // Two requirements per rule for a map (a track is a lat/lon pair), one
    // for every other panel type (a signal stands alone).
    const requirementsPerRule = type === PanelType.Map ? 2 : 1;
    if (type === PanelType.Map && requirements.length % 2 !== 0) {
      throw new Error(
        "A map panel's signals must be latitude/longitude pairs in role order.",
      );
    }
    const ruleIds: string[] = [];
    for (let i = 0; i < requirements.length; i += requirementsPerRule) {
      ruleIds.push(
        this.#layoutBuilder.addRule(
          requirements.slice(i, i + requirementsPerRule),
        ),
      );
    }

    this.#layoutBuilder.addPanel({ id, title, type }, ruleIds);
    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public addPlotPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    return this.addMultiSignalPanel({
      type: PanelType.Plot,
      signals: [
        {
          topicRecord: options.topicRecord,
          messagePathRecord: options.messagePathRecord,
        },
      ],
      id: options.id,
      title: options.title,
    });
  }

  public addRawMessagePanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    return this.addMultiSignalPanel({
      type: PanelType.RawMessage,
      signals: [
        {
          topicRecord: options.topicRecord,
          messagePathRecord: options.messagePathRecord,
        },
      ],
      id: options.id,
      title: options.title,
    });
  }

  public addThreeDPanel(options: {
    topicRecord: TopicRecord;
    messagePathRecord: MessagePathRecord;
    id?: string;
    title?: string;
  }): WorkspaceBuilder {
    const { topicRecord, messagePathRecord } = options;
    const id = options.id ?? crypto.randomUUID();
    const title =
      options.title ??
      `${topicRecord.topic_name}.${messagePathRecord.source_path}`;

    // TODO Add requirements for 3D panel
    this.#layoutBuilder.addPanel(
      {
        id,
        title,
        type: PanelType.ThreeD,
      },
      [],
    );

    this.#layoutBuilder.addPanelToLayout(id);

    return this;
  }

  public build(): LayoutNavigationState {
    const layout = this.#layoutBuilder.build();
    return {
      layoutDefinition: layout.state,
      schemaVersion: layout.schemaVersion,
    };
  }
}

/**
 * Utility function to create a workspace input for a given panel type.
 *
 * Usage:
 * ```ts
 * const workspaceInput = getWorkspaceInput(PanelType.Image, topicRecord, messagePathRecord);
 * ```
 */
export function getWorkspaceInput(
  panelType: PanelType,
  topicRecord: TopicRecord,
  messagePathRecord: MessagePathRecord,
) {
  const workspaceInput = new WorkspaceBuilder();

  switch (panelType) {
    case PanelType.Image:
      workspaceInput.addImagePanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.Log:
      workspaceInput.addLogPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.Map:
      workspaceInput.addMapPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.Plot:
      workspaceInput.addPlotPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.RawMessage:
      workspaceInput.addRawMessagePanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    case PanelType.ThreeD:
      workspaceInput.addThreeDPanel({
        topicRecord,
        messagePathRecord,
      });
      break;
    default:
      break;
  }

  return workspaceInput.build();
}
